import React, { useEffect, useRef, useState } from "react";
import Input from "./form/Input";
import useTranslationHook from "../hooks/useTranslationHook";
import { useDispatch, useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { useForm, useWatch } from "react-hook-form";
import { DeleteAccountSchema } from "../../utils/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    sendDeleteAccountOTP,
    verifyDeleteAccountOTP,
} from "../../redux/action/authAction";
import { handleDeleteAccountModal } from "../../redux/slice/modalSlice";

function DeleteAccountModal() {
    const { t } = useTranslationHook();
    const show = useSelector((state) => state.modal.deleteAccountModal);
    let lang = useSelector((state) => state.translation.lang);
    const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
    const otpInputs = useRef([]);
    const [step, setStep] = useState("form"); // form | otp

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        getValues,
        setValue,
    } = useForm({
        resolver: yupResolver(DeleteAccountSchema),
    });

    const [timer, setTimer] = useState(0);
    const dispatch = useDispatch();

    const selectedReason = useWatch({
        control,
        name: "reason",
    });

    const handleOtpChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otpValues];
        newOtp[index] = value;
        setOtpValues(newOtp);

        // Move to next input
        if (value && index < 5) {
            otpInputs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otpValues[index] && index > 0) {
            otpInputs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return;

        const newOtp = pastedData.split("");
        const updatedOtp = [...otpValues];

        for (let i = 0; i < 6; i++) {
            updatedOtp[i] = newOtp[i] || "";
        }

        setOtpValues(updatedOtp);
    };

    // Countdown logic
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const onSubmit = (data) => {
        dispatch(
            sendDeleteAccountOTP(
                {
                    email: data.email,
                    reason:
                        data.reason === "other"
                            ? data.otherReason
                            : data.reason,
                },
                t
            )
        ).then((res) => {
            if (res.error === 0) {
                setStep("otp");
                setTimer(30);
            }
        });
    };

    const handleResendOtp = () => {
        const data = getValues();
        dispatch(
            sendDeleteAccountOTP(
                {
                    email: data.email,
                    reason:
                        data.reason === "other"
                            ? data.otherReason
                            : data.reason,
                },
                t
            )
        );

        setTimer(30);
    };

    const handleClose = () => {
        dispatch(handleDeleteAccountModal(false));
    };

    const handleOtpSubmit = (data) => {
        const email = getValues("email");
        dispatch(
            verifyDeleteAccountOTP(
                {
                    otp: data.otp,
                    email,
                },
                t
            )
        ).then((res) => {
            if (res.error === 0) {
                handleClose();
            }
        });
    };

    const handleBackToForm = () => {
        setStep("form");
        setOtpValues(["", "", "", "", "", ""]);
        setTimer(0);
    };

    useEffect(() => {
        document.body.classList.toggle("modal-open", show);
        !show && reset();
        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [show]);

    useEffect(() => {
        if (selectedReason === "other") {
            setValue("otherReason", "");
        }
    }, [selectedReason]);

    return createPortal(
        <div
            className={`modal fade ${show ? "show d-block" : ""} purchase-modal`}
            tabIndex="-1"
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5
                            className="modal-title d-flex align-items-center"
                            style={{ gap: "12px" }}
                        >
                            {step === "otp" && (
                                <button
                                    type="button"
                                    onClick={handleBackToForm}
                                    aria-label={t("Back")}
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "50%",
                                        border: "none",
                                        background: "#f1f3f5",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        padding: 0,
                                        transition: "0.2s",
                                    }}
                                >
                                    <img
                                        src="/img/arrow-left.svg"
                                        alt={t("Back")}
                                        style={{
                                            width: "18px",
                                            height: "18px",
                                            transform:
                                                lang === "ar"
                                                    ? "rotate(180deg)"
                                                    : "none",
                                        }}
                                    />
                                </button>
                            )}

                            {t("Delete Account")}
                        </h5>

                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClose}
                            aria-label="Close"
                        />
                    </div>

                    <div className="modal-body pb-0">
                        {step === "form" && (
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                autoComplete="off"
                                autoCapitalize="off"
                            >
                                {/* Email */}
                                <div className="mb-3">
                                    <Input
                                        type="text"
                                        id="email"
                                        className="form-control"
                                        placeholder={t("Enter your email")}
                                        label={`${t("Email")} *`}
                                        register={register}
                                        errors={errors}
                                        {...register("email")}
                                    />
                                </div>

                                {/* Reason */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        {t("Reason")} *
                                    </label>
                                    <select
                                        className="form-select"
                                        {...register("reason")}
                                    >
                                        <option value="">
                                            {t("Select Reason")}
                                        </option>
                                        <option value="Privacy Concern">
                                            {t("Privacy Concern")}
                                        </option>
                                        <option value="Not Using Anymore">
                                            {t("Not Using Anymore")}
                                        </option>
                                        <option value="other">
                                            {t("Other")}
                                        </option>
                                    </select>

                                    {errors.reason && (
                                        <small className="text-danger">
                                            {errors.reason.message}
                                        </small>
                                    )}
                                </div>

                                {/* Other Reason */}
                                {selectedReason === "other" && (
                                    <div className="mb-3">
                                        <label className="form-label">
                                            {t("Please Specify")} *
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            {...register("otherReason")}
                                        />
                                        {errors.otherReason && (
                                            <small className="text-danger">
                                                {errors.otherReason.message}
                                            </small>
                                        )}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100"
                                >
                                    {t("Submit")}
                                </button>
                            </form>
                        )}

                        {step === "otp" && (
                            <form
                                autoComplete="off"
                                autoCapitalize="off"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    const finalOtp = otpValues.join("");
                                    if (finalOtp.length !== 6) return;
                                    handleOtpSubmit({ otp: finalOtp });
                                }}
                            >
                                <div className="text-center mb-4">
                                    <h6 className="fw-semibold mb-3">
                                        {t("Enter 6 Digit OTP")}
                                    </h6>

                                    <div
                                        onPaste={handlePaste}
                                        className="d-flex justify-content-center"
                                        style={{ gap: "12px" }}
                                    >
                                        {otpValues.map((digit, index) => (
                                            <input
                                                key={index}
                                                type="text"
                                                maxLength="1"
                                                value={digit}
                                                ref={(el) =>
                                                    (otpInputs.current[index] =
                                                        el)
                                                }
                                                onChange={(e) =>
                                                    handleOtpChange(
                                                        e.target.value,
                                                        index
                                                    )
                                                }
                                                onKeyDown={(e) =>
                                                    handleKeyDown(e, index)
                                                }
                                                style={{
                                                    width: "55px",
                                                    height: "60px",
                                                    fontSize: "22px",
                                                    textAlign: "center",
                                                    borderRadius: "15px",
                                                    border: "none",
                                                    outline: "none",
                                                    background: "#e0e5ec",
                                                    transition: "0.2s",
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Resend */}
                                <div className="text-center mb-3">
                                    <button
                                        type="button"
                                        disabled={timer > 0}
                                        onClick={handleResendOtp}
                                        className="btn btn-link p-0"
                                    >
                                        {timer > 0
                                            ? t("Resend OTP in {{time}}s", {
                                                  time: timer,
                                              })
                                            : t("Resend OTP")}
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={otpValues.join("").length !== 6}
                                    className="btn btn-primary w-100"
                                >
                                    {t("Verify OTP")}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.getElementById("root")
    );
}

export default DeleteAccountModal;
